import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  BottomNavigation,
  DefinedBenefitContributionBanner,
  PageContentHeaderDisplay,
} from 'enrollment/components';
import { useBenefitCategoryQuery } from 'enrollment/queries';
import {
  useEnrollmentContextQuery,
  EnrollmentContext,
  ENROLLMENT_COMPLETE_STATUS,
  useConfigurationQuery,
  BenefitCategory,
} from 'theSource/queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanId,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { setPerPayPeriodCost } from 'enrollment/components/Header/components/EnrollmentNavigation/services';
import { setEnrollmentLatest } from 'enrollment/components/BottomNavigation/services';
import { ROUTES } from 'enrollment/app/routes';
import { mapCategoriesData } from './utils';
import { PSBannerWrapper } from '../../../components/Shared';
import { queryKeyGenerator } from '../../../../theSource/utils';
import { setBenefitData } from 'enrollment/services/Benefit/slice';
import { selectHasPreExistingBenefitElections } from 'enrollment/services/CoveragesByCategory';
import { GenericPagePreview } from 'enrollment/features/Shared/PreviewContainers/GenericPagePreview';
import { GenericGroupingContainer } from 'enrollment/features/Shared/PreviewContainers/GenericGroupingContainer';
import { SurveyPopup } from 'enrollment/components/SurveyPopup';
import './BenefitsPreview.css';
import { getPlanYears } from 'enrollment/utils/common/Utils';
import { IneligbleBenefitsModal } from 'enrollment/components/IneligbleBenefitsModal';
import { CategoriesV2 } from '../../BenefitFlowV2';
import { BenefitsPreview, BenefitsPreviewProps } from './BenefitsPreview';
import {
  hasSavviBenefits,
  useBundledBenefitCategories,
} from 'enrollment/utils/bundledBenefitsUtils';

export const BundledBenefitsHub = ({ planYears }: BenefitsPreviewProps) => {
  // when the page loads we always show ALL ELIGIBLE BENEFITS
  const { t } = useTranslation('enrollment.benefits');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const savviBenefitCategories = useBundledBenefitCategories();
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContextType === 'life_event' ? orgPlanYearContextId : 0;
  const hasPreExistingBenefitElections = useSelector(
    selectHasPreExistingBenefitElections
  );
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const [allBenefitsElected, setAllBenefitsElected] = useState<boolean>(false);
  const [completeBenefits, setCompleteBenefits] = useState<boolean>(false);
  const [hasSavviElectedBenefits, setHasSavviElectedBenefits] = useState<
    boolean | undefined
  >(undefined);

  const { enrollmentContext: enrollmentContextResponse } =
    useEnrollmentContextQuery({
      enabled: true,
      apiHost: String(lumosApiHost),
    });
  let contexts: EnrollmentContext[] = [];
  let activeContextIndex = -1;

  if (enrollmentContextResponse) {
    contexts = enrollmentContextResponse?.data as EnrollmentContext[];
    activeContextIndex = Math.max(
      0,
      contexts.findIndex((context) => {
        return (
          !ENROLLMENT_COMPLETE_STATUS.includes(context.status) &&
          enrollmentContextType === context.enrollment_type_lookup_code
        );
      })
    );
    planYears = getPlanYears(contexts, activeContextIndex);
  }

  const benefitCategories: BenefitCategory[] =
    planYears && mapCategoriesData(planYears);
  const savviCategoryNames = savviBenefitCategories.flatMap(
    (category) => category.name
  );

  const { data, isFetching, isLoading } = useBenefitCategoryQuery({
    key: queryKeyGenerator(
      `benefits-${enrollmentContextType}`,
      orgPlanYearContextId
    ),
    enrollmentContextType: enrollmentContextType,
    orgPlanYearId: orgPlanYearContextId,
    enabled: enrollmentContextType !== '',
    lumosApiHost: String(lumosApiHost),
    getParamCategory: false,
  });

  const goToNextPage = () => {
    navigate(`/enrollment${ROUTES.enrollment.benefitDetails.path}`);
  };

  useEffect(() => {
    if (data) {
      setHasSavviElectedBenefits(
        hasSavviBenefits(data.data.benefits, data.data.bundled_org_benefit_ids)
      );
    }
    if (data && !isLoading && !isFetching && hasSavviElectedBenefits) {
      dispatch(setBenefitData({ benefitsData: data }));
      dispatch(
        setEnrollmentLatest(
          benefitCategories[benefitCategories.length - 1].lookup_code
        )
      );
      const allBenefitsHaveCoverage = data?.data.benefits.every(
        (benefit) =>
          Boolean(
            benefit.coverage &&
              (benefit.benefit.active_enrollment_required &&
              benefit.benefit.org_benefit_category !== 'survey' // active enrollment check if not survey since these benefits are on a different page
                ? data.data.active_enrollment_benefit_status[
                    benefit.benefit.org_benefit_id.toString()
                  ] || !benefit.benefit.active_enrollment_required
                : true)
          ) || benefit.benefit.benefit_lookup_code.includes('unum') // unum benefits don't require checkout
      );
      setAllBenefitsElected(allBenefitsHaveCoverage);

      if (allBenefitsHaveCoverage) {
        setCompleteBenefits(true);
      } else {
        setCompleteBenefits(false);
      }
      dispatch(setPerPayPeriodCost(Number(data.data.cart_total || 0)));
    }
    // it wants a const that will endlessly re-render so will ignore exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, dispatch, hasSavviElectedBenefits]);

  const scrollCheck = data !== undefined;
  useEffect(() => {
    // This code will run after the component has mounted
    const hash = window.location.hash.substring(1); // Get the anchor tag from the URL
    const element = document.getElementById(hash); // Get the element with the id of the anchor tag
    if (element) {
      const rect = element.getBoundingClientRect();
      const offset =
        window.innerWidth >= 744 && window.innerWidth <= 992 ? 180 : 100; // Adjust the offset based on the viewport width
      window.scrollTo(0, rect.top + window.scrollY - offset); // Scroll to the element with the adjusted offset
    }
  }, [scrollCheck]);

  if (isLoading) {
    return (
      <GenericPagePreview
        title={t('title')}
        containerDataTestId="benefit-preview-page"
        titleDataTestId="benefit-preview-title-loading"
      >
        <GenericGroupingContainer
          id="benefit-loading"
          groupName=""
          loading={true}
        >
          Loading...
        </GenericGroupingContainer>
      </GenericPagePreview>
    );
  }

  if (
    enrollmentContextType === 'life_event' ||
    (data && !hasSavviElectedBenefits)
  ) {
    return <BenefitsPreview planYears={planYears}></BenefitsPreview>;
  }

  return (
    <>
      <DefinedBenefitContributionBanner benefits={data} />
      <GenericPagePreview
        title={t('title')}
        containerDataTestId="benefit-preview-page"
        titleDataTestId="benefit-preview-title"
        blockClasses="mt-md-3_5 mt-lg-0 pt-md-4_5 pt-lg-4"
        id="mainContent"
      >
        <div className="page-content-mobile-padding">
          <PageContentHeaderDisplay
            pageContentLookupCode="enroll_benefits"
            displayPageHeader={false}
            orgPlanYearId={orgPlanYearId}
          />
        </div>
        {/* conditional based on at least 1 copied coverage existing */}
        {hasPreExistingBenefitElections[1] && (
          <PSBannerWrapper
            id="copied-elections-banner"
            message={t('copiedElectionsBanner')}
            level="info"
            icon
          />
        )}
        {data && (
          <>
            <CategoriesV2
              benefitCategories={savviCategoryNames}
              benefitCategoryData={data}
              currentBenefitCategory={savviCategoryNames[1]}
              benefitCategoryNames={savviCategoryNames}
              isLoading={isLoading || isFetching}
              allCategories={benefitCategories.map(
                (category) => category.lookup_code
              )}
            />
            <SurveyPopup categoryResponse={data} currentCategory="benefits" />
          </>
        )}
        <IneligbleBenefitsModal
          enrollmentContextType={enrollmentContextType}
          orgPlanYearContextId={orgPlanYearContextId}
          data={data}
          currentCategory="benefits"
        />
        {!completeBenefits && (
          <PSBannerWrapper
            id="incomplete-elections-banner"
            message={t('incompleteElectionsBanner')}
            level="info"
            icon
          />
        )}
        <BottomNavigation
          disableButton={!allBenefitsElected}
          translatedPage="enrollment.benefits"
          onClickEvent={goToNextPage}
        />
      </GenericPagePreview>
    </>
  );
};
